import { ReactElement } from 'react';
import {
  Badge,
  Box,
  ButtonGroup,
  Flex,
  FormLabel,
  InputGroup,
  InputLeftElement,
  Textarea,
  Tooltip,
} from '@chakra-ui/react';
import { faAsterisk, faEdit, faCheck, faEyeSlash, faFileSlash, faXmark } from '@fortawesome/pro-regular-svg-icons';
import { IconButtonWithTooltip, TextWithLinks } from 'shared-components';
import { ApplicantOfferRequirementTaskLevel, ApplicantOfferRequirementTaskStatus } from 'types';
import { taskStatusConfig, taskLevelConfig } from './requirement-rows';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

export enum NoteType {
  OfferLetterText = 'OfferLetterText',
  InternalNote = 'InternalNote',
}

type StatusType = ApplicantOfferRequirementTaskStatus | NoteType;

type NoteBaseProps = {
  status: StatusType;
  level?: ApplicantOfferRequirementTaskLevel;
  noteText: string;
  onEdit: () => void;
  id: string;
  index: number;
  disableEdit?: boolean;
  // Inline-edit props: used by callers not yet migrated to modal editing (OfferLetterText, Payment)
  editMode?: boolean;
  onSave?: () => void;
  onCancel?: () => void;
  onNoteChange?: (noteText: string) => void;
  disableSave?: boolean;
};

// editMode: true enables the legacy inline editor, currently only used by OfferLetterText.
// In that mode, onSave, onCancel, and onNoteChange are all required.
// All other callers use the modal pattern and must not pass those callbacks.
type NoteProps =
  | (NoteBaseProps & {
      editMode: true;
      onSave: () => void;
      onCancel: () => void;
      onNoteChange: (noteText: string) => void;
    })
  | (NoteBaseProps & { editMode?: false; onSave?: never; onCancel?: never; onNoteChange?: never });

// Unified config generator
const getNoteConfig = (status: StatusType, level?: ApplicantOfferRequirementTaskLevel) => {
  const baseStatusConfig = taskStatusConfig[status as ApplicantOfferRequirementTaskStatus];
  const conditionStatusConfig = taskStatusConfig[ApplicantOfferRequirementTaskStatus.Condition];
  const exemptStatusConfig = taskStatusConfig[ApplicantOfferRequirementTaskStatus.Exempt];

  const levelConfig = taskLevelConfig[level || ApplicantOfferRequirementTaskLevel.Condition];

  switch (status) {
    // 1. Task-level notes (driven by the level: Pre-offer, Condition, Enrolment)
    case ApplicantOfferRequirementTaskStatus.Condition:
    case ApplicantOfferRequirementTaskStatus.Assess: {
      const editTooltip = 'Edit task description';

      return {
        label: levelConfig.label,
        icon: levelConfig.icon || faAsterisk,
        colorScheme: levelConfig.colorScheme,
        editTooltip,
      };
    }

    // 2. Exemptions
    case ApplicantOfferRequirementTaskStatus.Exempt:
      return {
        label: 'Exempt reason',
        icon: exemptStatusConfig.icon || faFileSlash,
        colorScheme: baseStatusConfig?.colorScheme || 'gray',
        editTooltip: 'Edit exempt reason',
      };

    // 3. Offer Letter Text (Inherits from Condition status)
    case NoteType.OfferLetterText:
      return {
        label: '',
        icon: conditionStatusConfig.icon || faAsterisk,
        colorScheme: conditionStatusConfig.colorScheme || 'yellow',
        editTooltip: 'Edit offer letter text',
      };

    // 4. Internal Notes (Catches NotSatisfied, Satisfied, Provisional, and explicit InternalNotes)
    case ApplicantOfferRequirementTaskStatus.NotSatisfied:
    case ApplicantOfferRequirementTaskStatus.Satisfied:
    case ApplicantOfferRequirementTaskStatus.Provisional:
    case NoteType.InternalNote:
    default:
      return {
        label: 'Internal note',
        icon: faEyeSlash,
        colorScheme: 'gray',
        editTooltip: 'Edit internal note',
      };
  }
};

export const Note = (props: NoteProps): ReactElement => {
  const { status, level, noteText, onEdit, disableEdit, editMode } = props;

  const { label, icon, colorScheme, editTooltip } = getNoteConfig(status, level);

  const disableSave =
    props.disableSave ??
    (status === ApplicantOfferRequirementTaskStatus.Exempt && (!noteText || noteText.trim() === ''));

  const noteBadge = (
    <Badge variant={'transparent'} py={0} fontWeight={'medium'} gap={1}>
      {/* Badge within a Badge intentional, most efficient way to leverage colorScheme */}
      <Badge
        colorScheme={colorScheme}
        size={'xs'}
        borderRadius={'full'}
        px={0}
        height={4}
        width={4}
        justifyContent={'center'}
      >
        <FontAwesomeIcon icon={icon} color={'inherit'} fontSize={'10px'} fixedWidth />
      </Badge>

      {label ? `${label}:` : ''}
    </Badge>
  );

  // TODO: this should be removed and all edits done in a modal
  if (editMode) {
    return (
      <InputGroup size={'sm'}>
        <InputLeftElement
          as={FormLabel}
          htmlFor={`${props.id}-${props.index}-note-input`}
          color="muted"
          width={'auto'}
          px={6}
          py={2}
        >
          {noteBadge}
        </InputLeftElement>

        <Textarea
          id={`${props.id}-${props.index}-note-input`}
          value={noteText}
          onChange={e => props.onNoteChange(e.target.value)}
          pl={status === ApplicantOfferRequirementTaskStatus.Condition ? 32 : 36}
          py={1.5}
          mr={2}
          rows={2}
        />

        <ButtonGroup size={'sm'} spacing={2}>
          <Tooltip label="Note required" isDisabled={!disableSave}>
            <IconButtonWithTooltip
              label="Save"
              iconProps={{ icon: faCheck }}
              onClick={props.onSave}
              buttonProps={{ variant: 'solid', isDisabled: disableSave }}
            />
          </Tooltip>

          <IconButtonWithTooltip label="Cancel" iconProps={{ icon: faXmark }} onClick={props.onCancel} />
        </ButtonGroup>
      </InputGroup>
    );
  }

  return (
    <Flex className="group" alignItems={'start'} lineHeight={4} gap={1}>
      <Flex py={1} gap={1} alignItems={'start'} flexGrow={1}>
        {noteBadge}

        <Box flexGrow={1} fontSize={'sm'} color={'muted'} wordBreak="break-word">
          <TextWithLinks text={noteText} />
        </Box>
      </Flex>

      {!disableEdit && (
        <IconButtonWithTooltip
          label={editTooltip}
          data-selector="requirement-note-edit"
          iconProps={{ icon: faEdit }}
          onClick={onEdit}
          buttonProps={{
            variant: 'ghost',
            size: 'xs',
            visibility: 'hidden',
            _groupHover: { visibility: 'visible' },
          }}
        />
      )}
    </Flex>
  );
};
